/**
 * `/site/client-sign-in` — **Client sign-in** (C11, FR-J10, ADR-0028;
 * T-5.39). Moved under *Your website* by Wave 14 (C13, ADR-0032 §1) as it
 * was; `/settings/client-sign-in` redirects here.
 *
 * The firm chooses how its clients sign in to its portal: by **email**, by
 * **mobile**, or either (at least one — the segmented control has no *neither*
 * position); whether **new clients may create an account** or come by
 * invitation; and whether **Google** and **Microsoft** are offered. Beside it,
 * a live mock of the sign-in card as a client will see it, in the firm's
 * colours, following every switch before it is saved. That mock is not the
 * inline preview ADR-0032 §6 retired: it draws the platform's page, which no
 * draft of the website contains. One Save, on the edit bar (Wave 14E, T-4.66).
 *
 * **ProDesk builds no sign-in screen** (CLAUDE.md §7, ADR-0004). The choice is
 * published in `/site-brand.json` (12E) and the platform's `/accounts/` page
 * honours it (12P).
 *
 * **Since Wave 17 it is the rule, not a presentation** (C16, ADR-0035, which
 * supersedes ADR-0028 §4). The service writes it to the platform on every save
 * and the platform enforces it on the firm's hostname, so this page says what
 * is true (T-5.53):
 *
 * - **per mode**, what a new client is asked for and where the one code goes;
 * - **that it is enforced**, and what a change does to existing clients —
 *   asked once for what is newly required, never locked out;
 * - **per provider**, whose app clients will meet: *your firm's app* (the
 *   `identity_*` family is connected in Integrations), *FiveOX's app* (the
 *   fallback), or that we could not check;
 * - **when the platform has not confirmed** the rule — `meta.platform` on the
 *   read and the save, anything but `confirmed`.
 */

import { Alert, Badge, type BadgeTone, Skeleton, Switch } from '@fiveox/ui'
import { Info, KeyRound, LogIn, ShieldCheck, UserPlus } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router'

import { EditBar, useLeaveGuard } from '../../components/edit'
import { SETTINGS, useEvaluation } from '../../lib/access'
import { ApiError } from '../../lib/api'
import { type IntegrationCard, firm } from '../../lib/api/firm'
import { site } from '../../lib/api/site'
import {
  DEFAULT_SIGN_IN,
  type Identifier,
  type IdentifierMode,
  PROVIDER_LABELS,
  type Provider,
  type SignInChoice,
  identifierMode,
  identifiersFor,
  mockCard,
  signIn,
  signInProblem,
  toggleProvider,
} from '../../lib/api/signIn'
import { SettingsPage } from '../Firm/settings/SettingsPage'
import { Segmented } from '../Firm/settings/Segmented'
import { SiteSection } from './SiteSection'

interface Brand {
  name: string
  primary: string | null
  logoUrl: string | null
}

// -- what is true, as data (T-5.53) -----------------------------------------

/** The service's word on the platform's copy of the rule (`meta.platform`). */
export type PlatformStatus = 'confirmed' | 'pending' | 'unavailable' | 'not_configured'

const STATUSES: readonly PlatformStatus[] = ['confirmed', 'pending', 'unavailable', 'not_configured']

export function platformStatusOf(meta: Record<string, unknown> | undefined): PlatformStatus | null {
  const value = meta?.platform
  return STATUSES.includes(value as PlatformStatus) ? (value as PlatformStatus) : null
}

/** The notice for a rule the platform has not confirmed, or `null`. */
export function platformNotice(status: PlatformStatus | null): string | null {
  switch (status) {
    case 'pending':
      return 'Saved in ProDesk, but the platform hasn’t confirmed this rule yet — until it does, your clients may still meet the previous one. ProDesk sends it again each time it starts.'
    case 'unavailable':
      return 'Saved in ProDesk, but we couldn’t reach the platform to confirm it — until it’s confirmed, your clients may still meet the previous rule. ProDesk sends it again each time it starts.'
    case 'not_configured':
      return 'Saved in ProDesk, but this environment isn’t connected to the platform’s sign-in rules, so the platform hasn’t confirmed it and isn’t enforcing it here.'
    default:
      return null
  }
}

/** What a new client meets under each mode (ADR-0035 §2, spec §10.8 1–3). */
export function modeTruth(mode: IdentifierMode): {
  asked: string
  code: string
  notAsked: string | null
  later: string
} {
  switch (mode) {
    case 'email':
      return {
        asked: 'Their name, email address and a password.',
        code: 'One code, sent to their email.',
        notAsked: 'No mobile number is asked for, and no SMS or WhatsApp is sent.',
        later:
          'They sign in by email, with their password or an emailed code, and can add a mobile number in their profile, verified by a code.',
      }
    case 'mobile':
      return {
        asked: 'Their name, mobile number and a password.',
        code: 'One code, sent to their mobile by WhatsApp or SMS.',
        notAsked: 'No email address is asked for, and no email is sent.',
        later:
          'They sign in by mobile, and can add an email address in their profile, verified by a code. If the platform can’t send a code to a mobile number, sign-up is refused and they’re asked to contact you.',
      }
    default:
      return {
        asked: 'Their name, email address, mobile number and a password.',
        code: 'A code to each — one by email, one by WhatsApp or SMS — and both must be verified.',
        notAsked: null,
        later: 'They sign in with either.',
      }
  }
}

/** Identifiers `after` requires that `before` did not — what existing clients
 *  are asked for, once, at their next sign-in. */
export function newlyRequired(before: SignInChoice, after: SignInChoice): Identifier[] {
  return after.identifiers.filter((id) => !before.identifiers.includes(id))
}

/** Whose app a client meets on a provider's consent screen. */
export type ProviderApp = 'firm' | 'fiveox' | 'unavailable'

export function providerApp(provider: Provider, cards: IntegrationCard[] | null): ProviderApp {
  const card = cards?.find((c) => c.code === `identity_${provider}`)
  if (!card || card.state === 'unavailable') return 'unavailable'
  return card.state === 'active' ? 'firm' : 'fiveox'
}

const APP_COPY: Record<
  ProviderApp,
  { label: string; tone: BadgeTone; line: (name: string) => string }
> = {
  firm: {
    label: 'Your firm’s app',
    tone: 'success',
    line: (name) =>
      `${name} shows clients your firm’s own app, connected in Integrations, and they stay on your domain.`,
  },
  fiveox: {
    label: 'FiveOX’s app',
    tone: 'neutral',
    line: (name) =>
      `Until you connect your own ${name} app in Integrations, ${name} asks clients to allow FiveOX — the platform your portal runs on — rather than your firm.`,
  },
  unavailable: {
    label: 'Couldn’t check',
    tone: 'warning',
    line: () =>
      'We couldn’t reach the platform to see whether your own app is connected. Until it answers, assume FiveOX’s.',
  },
}

const IDENTIFIER_WORDS: Record<Identifier, string> = {
  email: 'an email address',
  mobile: 'a mobile number',
}

const same = (a: SignInChoice, b: SignInChoice) =>
  identifierMode(a) === identifierMode(b) &&
  a.allow_signup === b.allow_signup &&
  [...a.providers].sort().join() === [...b.providers].sort().join()

/** The sign-in card as the firm's clients will see it. Drawn from `mockCard`,
 *  so what the tests read is what is on the screen. */
export function SignInMock({ choice, brand }: { choice: SignInChoice; brand: Brand }) {
  const card = mockCard(choice)
  // The firm's own colour — data, not a design token — falls back to the
  // app's primary when the site has none.
  const accent = brand.primary ? { backgroundColor: brand.primary } : undefined
  const initials = brand.name
    .split(/\s+/)
    .filter((word) => /[A-Za-z]/.test(word))
    .slice(0, 2)
    .map((word) => word.match(/[A-Za-z]/)?.[0]?.toUpperCase() ?? '')
    .join('')
  return (
    <div
      data-sign-in-mock
      aria-label="Preview of your clients’ sign-in page"
      className="border-border bg-card overflow-hidden rounded-2xl border shadow-lg"
    >
      <div className="bg-primary h-1.5" style={accent} />
      <div className="space-y-5 p-6">
        <div className="flex items-center gap-3">
          {brand.logoUrl ? (
            <img src={brand.logoUrl} alt="" className="h-10 w-10 rounded-lg object-contain" />
          ) : (
            <span
              className="bg-primary flex h-10 w-10 items-center justify-center rounded-lg text-sm font-semibold text-white"
              style={accent}
            >
              {initials || 'F'}
            </span>
          )}
          <div className="min-w-0">
            <p className="text-foreground truncate font-semibold">{brand.name}</p>
            <p className="text-muted-foreground text-xs">Client portal</p>
          </div>
        </div>
        <div>
          <p className="text-foreground text-lg font-semibold">Sign in</p>
          <p className="text-muted-foreground text-sm">to see your documents, invoices and cases.</p>
        </div>
        <div className="space-y-1.5">
          <p className="text-foreground text-sm font-medium" data-mock-field>
            {card.fieldLabel}
          </p>
          <div className="border-input text-muted-foreground flex h-11 items-center rounded-lg border px-4 text-sm">
            {card.placeholder}
          </div>
        </div>
        <div
          className="bg-primary flex h-11 items-center justify-center rounded-lg text-sm font-semibold text-white"
          style={accent}
        >
          Continue
        </div>
        {card.providers.length > 0 && (
          <>
            <div className="text-muted-foreground flex items-center gap-3 text-xs">
              <span className="bg-border h-px flex-1" /> or <span className="bg-border h-px flex-1" />
            </div>
            <div className="space-y-2" data-mock-providers>
              {card.providers.map((name) => (
                <div
                  key={name}
                  className="border-input text-foreground flex h-11 items-center justify-center rounded-lg border text-sm font-medium"
                >
                  Continue with {name}
                </div>
              ))}
            </div>
          </>
        )}
        <p className="text-muted-foreground text-center text-sm" data-mock-register>
          {card.showRegister ? (
            <>
              New here? <span className="text-primary font-medium">Create an account</span>
            </>
          ) : (
            <>Accounts are by invitation from {brand.name}.</>
          )}
        </p>
      </div>
    </div>
  )
}

const MODES: Array<{ value: IdentifierMode; label: string }> = [
  { value: 'email', label: 'Email' },
  { value: 'mobile', label: 'Mobile' },
  { value: 'both', label: 'Both' },
]

export function ClientSignIn() {
  const { can } = useEvaluation()
  const editable = can(SETTINGS, 'can_update')
  const [original, setOriginal] = useState<SignInChoice | null>(null)
  const [draft, setDraft] = useState<SignInChoice>(DEFAULT_SIGN_IN)
  const [brand, setBrand] = useState<Brand>({ name: 'Your firm', primary: null, logoUrl: null })
  const [loadFailed, setLoadFailed] = useState(false)
  const [platform, setPlatform] = useState<PlatformStatus | null>(null)
  const [cards, setCards] = useState<IntegrationCard[] | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    signIn
      .read()
      .then((body) => {
        setOriginal(body.data)
        setDraft(body.data)
        setPlatform(platformStatusOf(body.meta))
      })
      .catch(() => {
        // Drawn with the default — what every firm's clients see until the
        // firm chooses (ADR-0028 §1) — and said, so Save is not a surprise.
        setLoadFailed(true)
        setOriginal(DEFAULT_SIGN_IN)
      })
    site
      .read()
      .then((body) =>
        setBrand({
          name: body.data.site.name || 'Your firm',
          primary: body.data.site.resolved_theme?.primary ?? null,
          logoUrl: body.data.site.logo_url,
        }),
      )
      .catch(() => undefined)
    // Whose Google and Microsoft app clients meet — T-5.54's two families. A
    // failed read is drawn as "couldn't check", never as FiveOX's.
    firm
      .integrations()
      .then((body) => setCards(body.data.cards))
      .catch(() => setCards([]))
  }, [])

  const dirty = useMemo(() => original !== null && !same(original, draft), [original, draft])
  const problem = signInProblem(draft)
  const guard = useLeaveGuard(dirty)

  const update = (next: SignInChoice) => setDraft(next)

  const save = async () => {
    if (problem) return
    setSaving(true)
    setError(null)
    try {
      const body = await signIn.save(draft)
      setOriginal(body.data)
      setDraft(body.data)
      setPlatform(platformStatusOf(body.meta))
      setLoadFailed(false)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Your choice could not be saved.')
    } finally {
      setSaving(false)
    }
  }

  const mode = identifierMode(draft)
  const truth = modeTruth(mode)
  const added = original ? newlyRequired(original, draft) : []
  const notice = platformNotice(platform)

  const providerRow = (provider: Provider, hint: string) => {
    const name = PROVIDER_LABELS[provider]
    const whose = providerApp(provider, cards)
    const app = APP_COPY[whose]
    return (
      <div className="flex items-start justify-between gap-4 py-4 first:pt-0 last:pb-0">
        <div data-provider={provider}>
          <p className="text-foreground flex flex-wrap items-center gap-2 text-sm font-medium">
            Sign in with {name}
            {cards !== null && (
              <span data-provider-app={whose}>
                <Badge tone={app.tone} size="sm">
                  {app.label}
                </Badge>
              </span>
            )}
          </p>
          <p className="text-muted-foreground mt-0.5 text-sm">{hint}</p>
          {cards !== null && <p className="text-muted-foreground mt-1 text-sm">{app.line(name)}</p>}
        </div>
        <Switch
          checked={draft.providers.includes(provider)}
          disabled={!editable}
          srLabel={`Sign in with ${name}`}
          onChange={(e) => update(toggleProvider(draft, provider, e.target.checked))}
        />
      </div>
    )
  }

  return (
    <SettingsPage
      back={{ to: '/site', label: 'Your website' }}
      title="Client sign-in"
      description="How your clients sign in and sign up to your client portal."
    >
      {guard}
      {error && <Alert tone="error">{error}</Alert>}
      {notice && (
        <div data-platform-notice={platform ?? undefined}>
          <Alert tone="warning">{notice}</Alert>
        </div>
      )}
      {loadFailed && (
        <Alert tone="warning">
          We couldn’t read your current choice, so this shows what every firm starts with — email or
          mobile, sign-up open, no Google or Microsoft. Saving sets it.
        </Alert>
      )}

      {original === null ? (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]" aria-busy="true">
          <Skeleton className="h-96 w-full rounded-2xl" />
          <Skeleton className="h-96 w-full rounded-2xl" />
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
          <div className="space-y-6">
            <SiteSection
              icon={<LogIn size={18} aria-hidden />}
              title="How clients identify themselves"
              description="What a new client is asked for, and where their one code goes."
            >
              <Segmented
                label="Clients sign in with"
                value={mode}
                options={MODES}
                disabled={!editable}
                onChange={(next) => update({ ...draft, identifiers: identifiersFor(next) })}
              />
              <dl className="mt-4 space-y-2 text-sm" data-mode-truth={mode}>
                <div>
                  <dt className="text-foreground font-medium">A new client gives</dt>
                  <dd className="text-muted-foreground">{truth.asked}</dd>
                </div>
                <div>
                  <dt className="text-foreground font-medium">To verify</dt>
                  <dd className="text-muted-foreground">
                    {truth.code}
                    {truth.notAsked ? ` ${truth.notAsked}` : ''}
                  </dd>
                </div>
                <div>
                  <dt className="text-foreground font-medium">After that</dt>
                  <dd className="text-muted-foreground">{truth.later}</dd>
                </div>
              </dl>
            </SiteSection>

            <SiteSection
              icon={<UserPlus size={18} aria-hidden />}
              title="New clients"
              description="Whether someone can make their own account, or waits for your invitation."
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-foreground text-sm font-medium">
                    Allow clients to create an account
                  </p>
                  <p className="text-muted-foreground mt-0.5 text-sm">
                    {draft.allow_signup
                      ? 'Anyone can sign up from your website’s Register link; you link them to a client.'
                      : 'Off: the Register link disappears from your website, and clients join when you invite them.'}
                  </p>
                </div>
                <Switch
                  checked={draft.allow_signup}
                  disabled={!editable}
                  srLabel="Allow clients to create an account"
                  onChange={(e) => update({ ...draft, allow_signup: e.target.checked })}
                />
              </div>
            </SiteSection>

            <SiteSection
              icon={<KeyRound size={18} aria-hidden />}
              title="Google and Microsoft"
              description="One-click sign-in for clients who already use either."
            >
              <div className="divide-border divide-y">
                {providerRow('google', 'For clients with a Gmail or Google Workspace address.')}
                {providerRow('microsoft', 'For clients with an Outlook or Microsoft 365 address.')}
              </div>
              <p className="text-muted-foreground mt-4 flex gap-2 text-sm">
                <Info size={16} className="mt-0.5 shrink-0" aria-hidden />
                <span>
                  Connect your firm’s own Google or Microsoft app in{' '}
                  <Link to="/settings/integrations" className="text-primary font-medium underline">
                    Integrations
                  </Link>
                  , as you connect your email or payment gateway.
                  {mode === 'mobile' &&
                    ' Because your clients sign in by mobile, the first time one uses Google or Microsoft they are asked for a mobile number and a code, once.'}
                </span>
              </p>
            </SiteSection>

            <SiteSection
              icon={<ShieldCheck size={18} aria-hidden />}
              title="Enforced on your domain"
              description="The platform holds your rule, not only your sign-in page."
            >
              <div className="text-muted-foreground space-y-3 text-sm" data-enforced>
                <p>
                  On your firm’s domain the platform refuses a sign-up, a sign-in, a code or a
                  password reset by a way you haven’t chosen — even from someone who goes round the
                  page.
                  {!draft.allow_signup && ' With sign-up off, it refuses new accounts there too.'}
                </p>
                <p>
                  When you change the rule nobody is locked out: a client who already has an account
                  signs in with what they have, and is asked once to add and verify what you now
                  require before their portal opens.
                </p>
                {added.length > 0 && (
                  <div data-newly-required={added.join(',')}>
                    <Alert tone="info">
                      Saving this asks each existing client without{' '}
                      {added.map((id) => IDENTIFIER_WORDS[id]).join(' or ')} to add one, verified by
                      a code, at their next sign-in.
                    </Alert>
                  </div>
                )}
              </div>
            </SiteSection>
          </div>

          <aside className="space-y-3 lg:sticky lg:top-6 lg:self-start">
            <p className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
              What your clients see
            </p>
            <SignInMock choice={draft} brand={brand} />
            <p className="text-muted-foreground text-xs">
              A preview. The real page is the platform’s, on your own domain, and follows what you
              save here.
            </p>
          </aside>
        </div>
      )}

      <EditBar
        dirty={dirty}
        saving={saving}
        problem={problem}
        editable={editable}
        onSave={() => void save()}
        onDiscard={() => original && setDraft(original)}
      />
    </SettingsPage>
  )
}
